import { isPast, isFuture, isSameDay, isValid, isBefore } from 'date-fns';
import { legacyParse } from 'forkedlibraries/date-fns-upgrade';
import { pluralizeWithoutValue } from 'utils/pluralize';

export const isEmpty = (value) =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

export const email = (value) => {
  if (
    !isEmpty(value) &&
    !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]*[^.]([a-zA-Z0-9.-]*[a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/i.test(
      value,
    )
  ) {
    return 'Invalid email address';
  }
};

export const required = (value) => {
  if (isEmpty(value)) {
    return 'Required';
  }
};

export const requireTrue = (value) => {
  if (value !== true) {
    return 'Required true';
  }
};

export const integer = (value) => {
  if (!isEmpty(value) && !Number.isInteger(Number(value))) {
    return 'Must be an integer';
  }
};

/*
  Set password validation method.
  Add regex here to check password string
  @params:
    value - string
      password field value
  @return:
    error string
*/
export const setPassword = (value) => {
  if (isEmpty(value)) {
    return 'Invalid password';
  }

  // const hasLowerCase = /[a-z]/.test(value);
  // const hasUpperCase = /[A-Z]/.test(value);
  // const hasDigits = /[\d]/.test(value);
  const hasLowerCase = true;
  const hasUpperCase = true;
  const hasDigits = true;
  const hasValidLength = value.length >= 6;

  if (hasDigits && hasUpperCase && hasLowerCase && hasValidLength) {
    return undefined;
  }

  return 'Invalid password';
};

/*
  Validate SSN input by regex matching
  @params:
    value - string
  @return:
    error string
*/
export function ssnValidation(value) {
  if (
    !isEmpty(value) &&
    !/^[0-9]{3}-?[0-9]{2}-?[0-9]{4}$/i.test(value) &&
    !/^xxx-xx-?[0-9]{4}$/i.test(value)
  ) {
    return 'Invalid SSN';
  }
}

export function ssnValidationFirstTime(value) {
  if (!isEmpty(value) && !/^[0-9]{3}-?[0-9]{2}-?[0-9]{4}$/i.test(value)) {
    return 'invalid SSN';
  }
}

/*
  Validate date input by parsing the input and creating a new Date
  @params:
    value - string
      mm/dd/yyyy (To-Do Akash: Add support for multiple date format validation)
  @return:
    error string
*/

export const dateValidationMMDDYY = (value) => {
  if (value.length > 9) {
    const isDateObj = legacyParse(value);
    if (!isValid(isDateObj)) {
      return 'Invalid Date';
    }
  } else if (!isEmpty(value) && value.toString().split('/').length === 3) {
    const [mm, dd, yy] = value.toString().split('/');

    if (yy.length === 2) {
      const dateObj = legacyParse(`20${yy}/${mm}/${dd}`);
      if (!isValid(dateObj)) {
        return 'Invalid Date';
      }
    } else {
      return 'Invalid';
    }
  } else {
    return 'Invalid';
  }
};

export const dateValidationMMYY = (value) => {
  if (isEmpty(value)) {
    return 'Expiration date is required.';
  }

  if (value.toString().split('/').length !== 2) {
    return 'Invalid expiration date format (MM/YY).';
  }

  const [mm, yy] = value.toString().split('/');

  if (yy.length !== 2) {
    return 'Invalid expiration date format (MM/YY).';
  }

  const month = Number(mm);
  if (month < 1 || month > 12) {
    return 'Invalid month. Please enter a value between 01-12.';
  }

  const dateObj = legacyParse(`20${yy}/${mm}/01`);

  if (!isValid(dateObj)) {
    return 'Invalid expiration date format (MM/YY).';
  }
};

export const isFutureDateMMYY = (value) => {
  const [mm, yy] = value.toString().split('/');
  const dateObj = legacyParse(`20${yy}/${mm}/01`);

  if (!isFuture(dateObj)) {
    return 'Card has expired. Please use a valid expiration date.';
  }
};

export const validPastYYYYString = (value) => {
  if (!isEmpty(value) && !Number.isInteger(Number(value))) {
    return 'Must be an integer';
  }

  const currentYear = new Date().getFullYear();
  if ((value && value.length < 4) || Number(value) > currentYear) {
    return 'Must be a valid year';
  }
};

export const validPastOrTodayMMDDYYDate = (value) => {
  const dateObj = legacyParse(new Date(value));

  if (isFuture(dateObj) && !isSameDay(dateObj, Date.now())) {
    return 'Is not in the past or today';
  }
};

export const validFutureOrTodayMMDDYYDate = (value) => {
  const dateObj = legacyParse(new Date(value));

  if (isBefore(dateObj, Date.now()) && !isSameDay(dateObj, Date.now())) {
    return 'Current date is in the past, please select a future date from today.';
  }
};

export const validFutureOrTodayMMYYDate = (value) => {
  const [mm, yy] = value.toString().split('/');
  const today = new Date();
  const dateObj = legacyParse(`20${yy}/${mm}/${today.getDate()}`);
  const isToday = isSameDay(dateObj, today);

  if (!isFuture(dateObj) && !isToday) {
    return 'Current date is in the past, please select a future date from today.';
  }
};

export const dateOfBirthValidation = (value) => {
  if (!isEmpty(value) && value.toString().split('/').length === 3) {
    const [mm, dd, yyyy] = value.toString().split('/');

    if (yyyy.length === 4) {
      const dateObj = legacyParse(`${yyyy}/${mm}/${dd}`);

      if (!isValid(dateObj)) {
        return 'Invalid Date';
      } else if (!isPast(dateObj)) {
        return 'Invalid Date';
      }
    } else {
      return 'Invalid';
    }
  } else {
    return 'Invalid';
  }
};

// Can't select next day if the current day is after noon/Can't select today

export const phoneValidation = (value) => {
  // stolen from: https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s02.html
  const phoneRegex = /^\d{10}$/;

  if (!isEmpty(value) && !phoneRegex.test(value)) {
    return 'Invalid';
  }
};

export const creditCard = (value) => {
  if (isEmpty(value)) {
    return 'Credit card number is required.';
  }

  if (integer(value)) {
    return 'Credit card number must contain only digits.';
  }

  const isAmex = /^34/.test(value) || /^37/.test(value);

  // condition Amex
  if (isAmex) {
    if (value.length !== 15) {
      return 'American Express cards must have 15 digits.';
    }
  } else if (value.length !== 16) {
    return 'Credit card number must have 16 digits.';
  }
};

export const zipcode = (value) => {
  if (isEmpty(value)) {
    return 'Billing ZIP code is required.';
  }

  if (integer(value)) {
    return 'ZIP code must contain only digits.';
  }

  if (value.length !== 5) {
    return 'ZIP code must be 5 digits.';
  }
};

export const creditCardCvc = (value, creditCardNumber) => {
  if (isEmpty(value)) {
    return 'Security code (CVC) is required.';
  }

  if (integer(value)) {
    return 'Security code must contain only digits.';
  }

  const isAmex = /^34/.test(creditCardNumber) || /^37/.test(creditCardNumber);

  // condition Amex
  if (isAmex) {
    if (value.trim().length !== 4) {
      return 'American Express security code must be 4 digits.';
    }
  } else if (value.trim().length !== 3) {
    return 'Security code (CVC) must be 3 digits.';
  }
};

// Used to add multiple validators together
export const composeValidators =
  (...validators) =>
  (value) =>
    validators.reduce(
      (error, validator) => error || validator(value),
      undefined,
    );

export const checkIfCurrentStatusIsSame = (requestedBy, isRenter) => {
  if (requestedBy === 'Resident' && isRenter === true) {
    return true;
  } else if (requestedBy === 'Homeowner' && !isRenter) {
    return true;
  }

  return false;
};

export const validateAddress = (value) => {
  const { addressOutsideUs } = value;

  if (addressOutsideUs) {
    if (isEmpty(value.completeAddress)) {
      return 'Invalid';
    }
  } else if (isEmpty(value.city)) {
    return 'Invalid';
  } else if (isEmpty(value.streetAddress?.trim())) {
    return 'Invalid';
  }
};

// Since we allow error messages on DateSlector, return true to show error border, but no message.

// Pass the minimum number of days required to pass the validation.
export const dateSelectorAtLeastNDaysSelected = (n) => (values) => {
  const daysSelected = values?.filter((value) => !!value?.times?.length);
  if (daysSelected.length < n) {
    return `Please select at least ${n} different ${pluralizeWithoutValue(n, 'day')}`;
  }
};
